import { StandardEvents } from '@shopify/events';

/**
 * Swaps a product-page block that is rendered per variant.
 *
 * The variant picker re-fetches the product page and only morphs itself.
 * Price, SKU, and downloads listen for that event. Specs and certification
 * icons sit in later sections, so they listen here and take their own
 * re-rendered markup from the same response.
 *
 * Family-level rows stay as they are because the server still prints them
 * from the product, not the SKU.
 */
class VariantBoundSection extends HTMLElement {
  connectedCallback() {
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @param {Event & { promise?: Promise<{ detail?: { html?: Document, productId?: string, newProduct?: { id: string } } }> }} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('product-card, quick-add-dialog, quick-add-component')) return;
    if (!event.target.closest('[data-testid="product-information"]')) return;
    if (!event.promise) return;

    const key = this.dataset.variantSection;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || !key) return;

        const { html, newProduct } = detail;
        if (newProduct) {
          this.dataset.productId = String(newProduct.id);
        } else if (detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const next = html.querySelector(`variant-bound-section[data-variant-section="${key}"]`);
        if (!next) {
          this.hidden = true;
          this.replaceChildren();
          return;
        }

        this.hidden = next.hasAttribute('hidden');
        this.replaceChildren(...Array.from(next.childNodes).map((node) => document.importNode(node, true)));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[variant-bound-section] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('variant-bound-section')) {
  customElements.define('variant-bound-section', VariantBoundSection);
}
